import { createTheme, alpha } from '@mui/material/styles'

// Custom color palette for LogarGo Admin Console
const primaryColor = '#6366f1' // Indigo
const secondaryColor = '#ec4899' // Pink
const successColor = '#10b981' // Emerald
const warningColor = '#f59e0b' // Amber
const errorColor = '#ef4444' // Red
const infoColor = '#3b82f6' // Blue

// Shipper color (business/store)
export const shipperColor = '#3b82f6' // Blue
// Freighter color (transport/truck)
export const freighterColor = '#8b5cf6' // Purple

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryColor,
      light: alpha(primaryColor, 0.8),
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: secondaryColor,
      light: alpha(secondaryColor, 0.8),
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    success: {
      main: successColor,
      light: alpha(successColor, 0.1),
      dark: '#059669',
    },
    warning: {
      main: warningColor,
      light: alpha(warningColor, 0.1),
      dark: '#d97706',
    },
    error: {
      main: errorColor,
      light: alpha(errorColor, 0.1),
      dark: '#dc2626',
    },
    info: {
      main: infoColor,
      light: alpha(infoColor, 0.1),
      dark: '#2563eb',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14, // Base font size (reduced from default 16)
    h1: {
      fontWeight: 700,
      fontSize: '2.125rem', // Reduced from 2.5rem
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem', // Reduced from 2rem
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.3125rem', // Reduced from 1.5rem
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem', // Reduced from 1.25rem
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '0.875rem', // Reduced from 1rem
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.8125rem', // Reduced from 0.875rem
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem', // Reduced from 0.9375rem
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem', // Reduced from 0.875rem
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem', // Reduced button font size
    },
    caption: {
      fontSize: '0.75rem', // Reduced caption size
    },
    overline: {
      fontSize: '0.75rem', // Reduced overline size
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          fontSize: '0.875rem', // Reduced button font size
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.8125rem', // Reduced chip font size
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // Reduced table cell font size
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f8fafc',
          fontSize: '0.8125rem', // Slightly smaller for headers
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha(primaryColor, 0.1),
            '&:hover': {
              backgroundColor: alpha(primaryColor, 0.15),
            },
          },
        },
      },
    },
  },
})

export const darkTheme = createTheme({
  ...lightTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#818cf8',
      light: '#a5b4fc',
      dark: '#6366f1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f472b6',
      light: '#f9a8d4',
      dark: '#ec4899',
      contrastText: '#ffffff',
    },
    success: {
      main: '#34d399',
      light: alpha('#34d399', 0.2),
      dark: '#10b981',
    },
    warning: {
      main: '#fbbf24',
      light: alpha('#fbbf24', 0.2),
      dark: '#f59e0b',
    },
    error: {
      main: '#f87171',
      light: alpha('#f87171', 0.2),
      dark: '#ef4444',
    },
    info: {
      main: '#60a5fa',
      light: alpha('#60a5fa', 0.2),
      dark: '#3b82f6',
    },
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#94a3b8',
    },
    divider: '#334155',
  },
  components: {
    ...lightTheme.components,
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // Reduced table cell font size
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#1e293b',
          fontSize: '0.8125rem', // Slightly smaller for headers
        },
      },
    },
  },
})

// Shipment status colors
export const shipmentStatusColors: Record<string, { bg: string; text: string; label: string }> = {
  PENDING: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
  MATCHING: { bg: '#dbeafe', text: '#1e40af', label: 'Matching' },
  MATCHED: { bg: '#d1fae5', text: '#065f46', label: 'Matched' },
  PICKUP_SCHEDULED: { bg: '#e0e7ff', text: '#3730a3', label: 'Pickup Scheduled' },
  IN_TRANSIT: { bg: '#cffafe', text: '#155e75', label: 'In Transit' },
  DELIVERED: { bg: '#dcfce7', text: '#166534', label: 'Delivered' },
  COMPLETED: { bg: '#d1fae5', text: '#065f46', label: 'Completed' },
  CANCELLED: { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' },
  DISPUTED: { bg: '#fce7f3', text: '#9d174d', label: 'Disputed' },
}

// Score color helper
export function getScoreColor(score: number): string {
  if (score >= 850) return '#fbbf24' // Gold
  if (score >= 700) return '#3b82f6' // Blue
  if (score >= 500) return '#10b981' // Green
  if (score >= 300) return '#f59e0b' // Yellow/Orange
  return '#ef4444' // Red
}

